import { FileText, Printer } from "lucide-react";
import ScoreGauge from "../ScoreGauge";
import Timeline from "../Timeline";
import { photoUrl } from "../../api";
import {
  PAINT_PARTS, PAINT_META, MECH_ITEMS, ELEC_ITEMS, CHECK_STATUS_META,
  TIRE_POSITIONS, PHOTO_CATEGORIES, fmtKm, fmtDate, fmtTry, SEVERITY_LABELS,
} from "../../lib/labels";

function ChipList({ items, source, meta, defaultStatus }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const v = source[it.key] || {};
        const status = typeof v === "string" ? v : (v.status || defaultStatus);
        const m = meta[status] || meta[defaultStatus];
        return (
          <span key={it.key} className={`text-xs px-3 py-1.5 rounded-full border ${m.cls}`}>
            {it.label}: <b>{m.label}</b>
          </span>
        );
      })}
    </div>
  );
}

function ReportTitle({ children }) {
  return <h4 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-3">{children}</h4>;
}

function ReportPhotos({ photos }) {
  if (photos.length === 0) return null;
  return (
    <div className="mt-6">
      <ReportTitle>Araç Fotoğrafları</ReportTitle>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.map((p) => (
          <div key={`${p.path}|${p.label}`} className="relative">
            <img src={photoUrl(p.path)} alt="" className="w-full h-28 object-cover rounded-lg border border-white/10" />
            {p.label && <span className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-slate-200">{p.label}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportChips({ title, items, source, meta, defaultStatus }) {
  return (
    <div className="mt-6">
      <ReportTitle>{title}</ReportTitle>
      <ChipList items={items} source={source} meta={meta} defaultStatus={defaultStatus} />
    </div>
  );
}

function ReportRecords({ title, records, renderLine }) {
  return (
    <div className="mt-6">
      <ReportTitle>{title} ({records.length})</ReportTitle>
      {records.length === 0 ? <p className="text-sm text-slate-500">Kayıt yok.</p> : (
        <div className="space-y-2">{records.map(renderLine)}</div>
      )}
    </div>
  );
}

export default function ReportSection({ vehicle }) {
  const gallery = vehicle.photo_gallery || [];
  const allPhotos = [...(vehicle.photos || []).map((p) => ({ path: p, label: "" })),
    ...gallery.map((g) => ({ path: g.path, label: (PHOTO_CATEGORIES.find((c) => c.value === g.category) || {}).label || "" }))];
  const bakim = (vehicle.history || []).filter((h) => h.type === "servis").sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const hasar = (vehicle.history || []).filter((h) => ["kaza", "hasar"].includes(h.type)).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const timeline = [...(vehicle.history || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div data-testid="section-rapor" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-8">
      <div className="flex items-center justify-between gap-3 flex-wrap no-print">
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <FileText className="w-5 h-5 text-cyan-300" /> Araç Raporu
        </h3>
        <button data-testid="detail-print-button" onClick={() => window.print()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition-all text-sm">
          <Printer className="w-4 h-4" /> Yazdır / PDF
        </button>
      </div>

      <div className="mt-6 border-b border-white/10 pb-6">
        <p className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400">OTO KÜNYE — Araç Durum Raporu</p>
        <h2 className="mt-2 text-2xl font-extrabold text-white">{vehicle.brand} {vehicle.model} <span className="text-slate-400">{vehicle.year}</span></h2>
        <p className="mt-1 font-mono-vin text-sm text-cyan-300 tracking-widest">{vehicle.vin}</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          {[["Plaka", vehicle.plate], ["Kilometre", fmtKm(vehicle.km)], ["Motor", vehicle.engine], ["Yakıt", vehicle.fuel],
            ["Vites", vehicle.transmission], ["Renk", vehicle.color]].map(([l, v]) => (
            <div key={l}><p className="text-xs text-slate-500">{l}</p><p className="font-semibold text-slate-100">{v || "Belirtilmedi"}</p></div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <ScoreGauge score={vehicle.score} label={vehicle.score >= 80 ? "Mükemmel" : vehicle.score >= 60 ? "İyi" : vehicle.score >= 40 ? "Orta Risk" : "Yüksek Risk"} size={160} />
      </div>

      <ReportPhotos photos={allPhotos} />

      <ReportChips title="Kaporta / Boya Durumu" items={PAINT_PARTS} source={vehicle.paint || {}} meta={PAINT_META} defaultStatus="belirtilmedi" />

      <ReportChips title="Mekanik Durum" items={MECH_ITEMS} source={vehicle.mechanics || {}} meta={CHECK_STATUS_META} defaultStatus="kontrol_edilmedi" />

      <ReportChips title="Elektrik / Elektronik" items={ELEC_ITEMS} source={vehicle.electrics || {}} meta={CHECK_STATUS_META} defaultStatus="kontrol_edilmedi" />

      <div className="mt-6">
        <h4 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-3">Lastik / Jant</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {TIRE_POSITIONS.map((p) => {
            const t = (vehicle.tires || {})[p.key] || {};
            return (
              <p key={p.key} className="text-slate-300">
                <b>{p.label}:</b> {[t.marka, t.olcu, t.durum].filter(Boolean).join(" • ") || "Belirtilmedi"}
              </p>
            );
          })}
          <p className="text-slate-300 sm:col-span-2"><b>Jantlar:</b> {(vehicle.rims && vehicle.rims.durum) || "Belirtilmedi"}{(vehicle.rims && vehicle.rims.aciklama) ? ` — ${vehicle.rims.aciklama}` : ""}</p>
        </div>
      </div>

      <ReportRecords title="Bakım Geçmişi" records={bakim} renderLine={(r) => (
        <p key={r.id} className="text-sm text-slate-300">
          <span className="text-slate-500">{fmtDate(r.date)}</span> — <b>{r.title}</b>
          {r.km != null && ` • ${fmtKm(r.km)}`}{r.servis && ` • ${r.servis}`}{fmtTry(r.cost) && ` • ${fmtTry(r.cost)}`}
        </p>
      )} />

      <ReportRecords title="Hasar / Kaza Geçmişi" records={hasar} renderLine={(r) => (
        <p key={r.id} className="text-sm text-slate-300">
          <span className="text-slate-500">{fmtDate(r.date)}</span> — <b>{r.title}</b>
          {r.zone && ` • ${r.zone}`}{r.damage_type && ` • ${r.damage_type}`}
          {r.severity && ` • ${SEVERITY_LABELS[r.severity] || r.severity}`}{fmtTry(r.cost) && ` • ${fmtTry(r.cost)}`}
        </p>
      )} />

      {vehicle.notes && (
        <div className="mt-6">
          <h4 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-3">Notlar</h4>
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{vehicle.notes}</p>
        </div>
      )}

      <div className="mt-8">
        <h4 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-6">Zaman Çizelgesi</h4>
        <Timeline history={timeline} />
      </div>
    </div>
  );
}
